import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { RuleEngineService } from "src/app/services/rule-engine.service";
import { ToastrService } from "ngx-toastr";
import { debug } from "console";
import {
  ConditionGroup,
  NestedGroups,
  PredicateData,
  REMethodNames,
  REMethodParameters,
  ReturnResponse,
  Rule,
} from "../models/rule-engine";
import { DataParserService } from "src/app/services/data-parser.service";
import { map, Observable, startWith, Subscription } from "rxjs";

@Component({
  selector: "app-custom-rules",
  templateUrl: "./custom-rules.component.html",
  styleUrls: ["./custom-rules.component.scss"],
})
export class CustomRulesComponent implements OnInit, OnDestroy {
  public methodNames: string[] = [];
  public methodParametersData: any[] = [];
  public customRules: FormGroup;
  public selectedMethodName = new FormControl("", Validators.required);
  public apNodePaths: string[] = [];
  public cpNodePaths: string[] = [];
  public allNodePaths: string[] = [];
  filteredOptionsMap: Map<number, Observable<string[]>> = new Map();
  private openPredicateFilter: boolean = false;
  public xPathsWithLeafNodes: any;
  private defaultLogicalOperatorForSimpleFilter: string = "AND";
  private indexOfTheFieldToUpdateThePath: number;
  private rules: Rule[] = [];
  public rulename = new FormControl("", Validators.required);
  private _rulesSubjectSubscription!: Subscription;
  private _updateFieldWithPredicateConditionsSubscription!: Subscription;
  private _nodesWithTheirLeafNodesSubscription!: Subscription;
  public  _isEdit = false;

  constructor(
    public dialogRef: MatDialogRef<CustomRulesComponent>,
    @Inject(MAT_DIALOG_DATA) public incomingData: Rule,
    private _fb: FormBuilder,
    private ruleEngineService: RuleEngineService,
    private toastr: ToastrService,
    private _dataParserService: DataParserService,
    private _ruleEngineService: RuleEngineService
  ) {}

  ngOnInit(): void {
    this.customRules = this._fb.group({
      parameters: this._fb.array([]),
    });

    if (this.incomingData) {
      this._isEdit = true;
      let param = this.incomingData.param;
      let paramsArray = param.split("|");
      let methodName = this.incomingData.field;

      // Set the method name and rule name
      this.selectedMethodName.setValue(methodName);
      this.rulename.setValue(this.incomingData.rulename);

      this._ruleEngineService.getMethodParameters(methodName).subscribe({
        next: (response: ReturnResponse<REMethodParameters[]>) => {
          //first we will clear the old lables if present
          this.methodParametersData = [];
          //we are storing the label separately
          this.methodParametersData = response.responsebody.map(
            (methodParam) => methodParam
          );

          // Clear existing parameters
          this.parameters.clear();

          // Add form controls with values from paramsArray
          paramsArray.forEach((paramValue, index) => {
            this.parameters.push(
              this._fb.control(paramValue, Validators.required)
            );

            // If it's an XPath field, setup its filtered options
            if (this.methodParametersData[index].ShowXPath) {
              this.filteredOptionsMap.set(
                index,
                this.parameters.controls[index].valueChanges.pipe(
                  startWith(""),
                  map((value) => this._filter(value, this.allNodePaths))
                )
              );
            }
          });
        },
      });
    }

    this.ruleEngineService.getMethodNames().subscribe({
      next: (response: ReturnResponse<REMethodNames>) => {
        this.methodNames = response.responsebody.MethodNames;
      },
      error: (err) => {
        this.toastr.error("Unable to get method names", "", {
          timeOut: 4000,
          positionClass: "toast-bottom-right",
        });
      },
    });

    this._nodesWithTheirLeafNodesSubscription =
      this._dataParserService.$getNodesWithTheirLeafNodes.subscribe({
        next: (data) => (this.xPathsWithLeafNodes = data),
        error: (err) => {
          this.toastr.error("Unable to get leaf nodes", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });

    this._updateFieldWithPredicateConditionsSubscription =
      this._ruleEngineService.$getUpdatedFieldWithPredicate.subscribe({
        next: (data) => {
          // Only set value if the control exists and index is valid
          if (
            this.parameters?.controls &&
            this.indexOfTheFieldToUpdateThePath !== undefined &&
            this.parameters.controls[this.indexOfTheFieldToUpdateThePath]
          ) {
            this.parameters.controls[
              this.indexOfTheFieldToUpdateThePath
            ].setValue(data);
          }
        },
        error: (err) => {
          this.toastr.error("Unable to get field", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });

    this._rulesSubjectSubscription = this._ruleEngineService
      .getAllRules()
      .subscribe({
        next: (data) => (this.rules = data ?? []),
        error: (err) => {
          this.toastr.error("Unable to get rules", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });

    //load the AP and CP data
    this.apNodePaths = this._dataParserService.APNodePaths;
    this.cpNodePaths = this._dataParserService.CPNodePaths;
    // Use Set to remove duplicates when combining paths
    this.allNodePaths = [...new Set([...this.apNodePaths, ...this.cpNodePaths])];
  }

  ngOnDestroy(): void {
    if (this._rulesSubjectSubscription) {
      this._rulesSubjectSubscription.unsubscribe();
    }
    if (this._updateFieldWithPredicateConditionsSubscription) {
      this._updateFieldWithPredicateConditionsSubscription.unsubscribe();
    }
    if (this._nodesWithTheirLeafNodesSubscription) {
      this._nodesWithTheirLeafNodesSubscription.unsubscribe();
    }
  }

  onClickClose() {
    this.dialogRef.close();
  }
  onSubmit() {
    this.selectedMethodName.markAsTouched();
    this.rulename.markAsTouched();
    this.customRules.markAllAsTouched();
    if (
      this.customRules.valid &&
      this.selectedMethodName.valid &&
      this.rulename.valid
    ) {
      if (this._isEdit) {
        // Check for duplicate rule names excluding the current rule
        const isDuplicate = this.rules.some(
          (rule) => 
            rule.rulename === this.rulename.value && 
            rule.rulenumber !== this.incomingData.rulenumber
        );

        if (isDuplicate) {
          this.toastr.warning("Rule name already exists", "", {
            timeOut: 3000,
            positionClass: "toast-bottom-right",
          });
        } else {
          // Update the rule
          const updatedRule: Rule = {
            rulenumber: this.incomingData.rulenumber,
            ruletype: "Method",
            param: this.generateParam(),
            rulename: this.rulename.value,
            operator: "IN",
            value: "true",
            field: this.selectedMethodName.value,
          };

          // Find and update the rule in the rules array
          const index = this.rules.findIndex(
            (rule) => rule.rulenumber === this.incomingData.rulenumber
          );
          if (index !== -1) {
            this.rules[index] = updatedRule;
            this._ruleEngineService.setAllTheRules(this.rules);
            this.onClickClose();
          }
        }
      } else {
        if (!this.checkEditDupliate()) {
          this.addRule();
          this.onClickClose();
        }
      }
    } else {
      this.toastr.warning("Please fill all required fields", "", {
        timeOut: 3000,
        positionClass: "toast-bottom-right",
      });
    }
  }
  private addRule() {
    let rule: Rule = {
      rulenumber: this.rules.length + 1,
      ruletype: "Method",
      param: this.generateParam(),
      rulename: this.rulename.value,
      operator: "IN",
      value: "true",
      field: this.selectedMethodName.value,
    };
    this.rules.push(rule);
    this._ruleEngineService.setAllTheRules(this.rules);
  }
  private checkEditDupliate(): boolean {
    let isDuplicate = false;
    if (
      this.rules.map((rule: Rule) => {
        if (rule.rulename === this.rulename.value) {
          isDuplicate = true;
          this.toastr.warning("Rule name already exists", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        }
      })
    )
      return isDuplicate;
  }
  private generateParam(): string {
    return this.parameters.controls
      .map((param) => param.value.trim())
      .join("|");
  }
  get parameters(): FormArray {
    return this.customRules.get("parameters") as FormArray;
  }

  addParameter() {
    this.parameters.push(this._fb.control("", Validators.required));
  }
  onSelectionChange() {
    this.ruleEngineService
      .getMethodParameters(this.selectedMethodName.value)
      .subscribe({
        next: (response: ReturnResponse<REMethodParameters[]>) => {
          //first we are clearing the older parameters
          this.parameters.clear();

          //add one formcontrol for each of the item with an identifier
          response.responsebody.forEach((param, index) => {
            this.parameters.push(this._fb.control("", Validators.required));

            // If it's an XPath field, setup its filtered options
            if (param.ShowXPath) {
              // Create filtered options for this specific control
              this.filteredOptionsMap.set(
                index,
                this.parameters.controls[index].valueChanges.pipe(
                  startWith(""),
                  map((value) => this._filter(value, this.allNodePaths))
                )
              );
            }
          });

          //first we will clear the old lables if present
          this.methodParametersData = [];
          //we are storing the label separately
          this.methodParametersData = response.responsebody.map(
            (methodParam) => methodParam
          );
        },
        error: (err) => {
          this.toastr.error("Unable to get parameters", "", {
            timeOut: 4000,
            positionClass: "toast-bottom-right",
          });
        },
      });
  }
  private _filter(value: string, options: string[]): string[] {
    const filterValue = value?.toLowerCase() || "";
    return options.filter((option) =>
      option.toLowerCase().includes(filterValue)
    );
  }

  //#region This region is responsible for opening the predicate filter component
  onDoubleClick(index: number) {
    this.indexOfTheFieldToUpdateThePath = index;
    //this will split the path in the filed form control based on the '/'
    let individualNodes = this.parameters.controls[index].value.split("/");
    //we are storing the last part of the xpath from the user entered field as we need it later to check if the xpath actually exists in the list of all the xpaths we have got from the xml template
    let lastPartOfTheField = individualNodes[individualNodes.length - 1];
    //here will get all the nodes and their filters as key and values
    let kvpOfNodeAndFilters: { [key: string]: string | null }[] =
      this.getKeyValueOfNodeAndFilter(individualNodes);
    //here we will check if the xpath after removing all the filters will match with any of the xpaths that we have generated from the xml template
    let allowEdit = this.isPathMatch(kvpOfNodeAndFilters, lastPartOfTheField);
    if (!allowEdit) {
      this.toastr.warning("The path is not present in AP or CP", "", {
        timeOut: 4000,
        positionClass: "toast-bottom-right",
      });

      //here we are trying to make sure that the predicate filter does not open because there is no matching path in the CP or AP
      this.openPredicateFilter = false;
      this._ruleEngineService.setPopUpValue(this.openPredicateFilter);
    } else {
      let xpathWithoutFilters =
        this.getXPathWithoutFilters(kvpOfNodeAndFilters);
      let xPaths = this.generateXPathsFromField(xpathWithoutFilters);
      let listOfPredicateData = this.getNodesWithLeaves(xPaths);
      let hasFilters = this.hasFilters(kvpOfNodeAndFilters);
      if (hasFilters) {
        let finalPredicateData = this.generateTheDataWhenFilters(
          kvpOfNodeAndFilters,
          listOfPredicateData
        );
        this._ruleEngineService.dataToSendForPredicatePopUp =
          finalPredicateData;
        this._ruleEngineService.setSubConditionsFor(
          xpathWithoutFilters,
          this.xPathsWithLeafNodes,
          this.allNodePaths
        );
      } else {
        let finalPredicateData =
          this.generateTheDataWhenNoFilters(listOfPredicateData);
        this._ruleEngineService.dataToSendForPredicatePopUp =
          finalPredicateData;
        this._ruleEngineService.setSubConditionsFor(
          xpathWithoutFilters,
          this.xPathsWithLeafNodes,
          this.allNodePaths
        );
      }

      //here we are trying to make sure that the predicate filter does open because there is matching path in the CP or AP
      this.openPredicateFilter = true;
      this._ruleEngineService.setPopUpValue(this.openPredicateFilter);
    }
  }
  //this method will take user entered xpath and return all the nodes as key and all the filters as their values
  getKeyValueOfNodeAndFilter(
    individualNodes: string[]
  ): { [key: string]: string | null }[] {
    let kvpOfNodeAndFilters: { [key: string]: string | null }[] = [];
    individualNodes.map((node: any) => {
      let regex = /([^\[]+)(\[[^\]]+\])?/;
      //first checking if the node has any filters by using the regular expression then returning true or false based on that
      let isMatch = regex.test(node);
      //take the first node add the node as key and its filters as value to the kvpOfNodeAndFilters variable
      if (isMatch) {
        const match = node.match(regex);
        if (match[2]) {
          let key = match[1];
          let value = match[2];
          kvpOfNodeAndFilters.push({ [key]: value });
        } else {
          let key = match[1];
          let value = null;
          kvpOfNodeAndFilters.push({ [key]: value });
        }
      }
    });
    return kvpOfNodeAndFilters;
  }

  //this method checks if the given path matches with the xpaths we have generated from xml. returns true if it matches or false
  isPathMatch(
    kvpOfNodeAndFilters: { [key: string]: string | null }[],
    lastPartOfTheField: string
  ): boolean {
    //here we are generating the plain xpath without the filters to compare and them with the xpaths we have generated from the loaded xml
    let xpathWithoutFilters = this.getXPathWithoutFilters(kvpOfNodeAndFilters);
    if (this.allNodePaths.includes(xpathWithoutFilters)) {
      return true;
    } else {
      return false;
    }
  }

  getXPathWithoutFilters(
    kvpOfNodeAndFilters: { [key: string]: string | null }[]
  ): string {
    let xpathWithoutFilters: string = "";
    kvpOfNodeAndFilters.map((node: any, index: number) => {
      xpathWithoutFilters += Object.keys(node)[0];
      if (index !== kvpOfNodeAndFilters.length - 1) {
        xpathWithoutFilters += "/";
      }
    });
    //here we are manually adding the last part of the xpath that we have extracted earlier to finally arrive at the xpath which we need to compare with the xpaths generated form the xml
    xpathWithoutFilters;
    return xpathWithoutFilters;
  }

  //this will generate multiple xpaths from the xpath provided by the user.
  //Eg: Case/OrderingProvider/Location[@AccountType='Ordering Facility']/@OrganizationGUID
  //Output will be array of strings of these values: "Case", "Case/OrderingProvider", "Case/OrderingProvider/Location"
  generateXPathsFromField(xPathsWithoutFilters: string): string[] {
    let xPathWithOnlyParentNodes = xPathsWithoutFilters
      ?.split("/")
      .slice(0, -1);
    if (
      !xPathWithOnlyParentNodes ||
      !Array.isArray(xPathWithOnlyParentNodes) ||
      xPathWithOnlyParentNodes.length === 0
    ) {
      return []; // Return an empty array if input is invalid
    }

    let xPaths: string[] = [];
    let currentPath = "";

    for (const path of xPathWithOnlyParentNodes) {
      currentPath = currentPath ? `${currentPath}/${path}` : path;
      xPaths.push(currentPath);
    }
    return xPaths;
  }
  getNodesWithLeaves(xPaths: string[]): PredicateData[] {
    let predicatData: PredicateData;
    if (!Array.isArray(xPaths) || !Array.isArray(this.xPathsWithLeafNodes)) {
      return []; // Return an empty array if inputs are invalid
    }

    // Filter `allTheNodes` where `nodeName` exists in `generatedPaths`
    const predicateData = this.xPathsWithLeafNodes
      .filter((node) => xPaths.includes(node.nodeName)) // Filter matching paths
      .map((node) => {
        const parts = node.nodeName.split("/"); // Split the path by '/'
        const tabName = parts[parts.length - 1]; // Get the last segment of the path
        return (predicatData = {
          nodeName: tabName, // Use the last part as the nodeName
          attributes: node.leafNodes, // Keep the original leafNodes
          nodePath: node.nodeName, // Include the full path as nodePath
        });
      });
    return predicateData;
  }

  hasFilters(kvpOfNodeAndFilters: { [key: string]: string | null }[]): boolean {
    let hasFilters = kvpOfNodeAndFilters.some((item: any) => {
      return Object.values(item).some(
        (value: any) => value !== null && value !== ""
      );
    });
    return hasFilters;
  }

  //here we are adding the form group properly based on the filters present
  generateTheDataWhenFilters(
    kvpOfNodeAndFilters: { [key: string]: string | null }[],
    listOfPredicateData: PredicateData[]
  ): PredicateData[] {
    let isSimple: boolean = false;
    let isGroup: boolean = false;
    let isNested: boolean = false;
    let predicateDataWithFormData: PredicateData[] = [];
    let attributes: any[] = [];
    kvpOfNodeAndFilters.forEach((kvp: { [key: string]: string | null }) => {
      const key = Object.keys(kvp)[0];
      const value = kvp[key];
      listOfPredicateData.forEach((predicateData: PredicateData) => {
        if (key === predicateData.nodeName && value !== null && value !== "") {
          isSimple = this.checkForSimpleFilter(value);
          isGroup = this.checkForGroupFilter(value);
          isNested = this.checkForNestedFilter(value);
          let matchingNodes = this.xPathsWithLeafNodes.filter(
            (node: any) => node.nodeName === predicateData.nodePath
          );
          matchingNodes?.length > 0
            ? (attributes = matchingNodes[0].leafNodes)
            : (attributes = []);

          if (isSimple) {
            let formData = this.generateFormDataForSimpleFilter(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          } else if (isGroup) {
            let formData = this.generateFormDataForGroup(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          } else if (isNested) {
            let formData = this.generateFormDataForNestedFilter(value);
            let predicateWithFormData: PredicateData = {
              nodeName: predicateData.nodeName,
              nodePath: predicateData.nodePath,
              attributes: predicateData.attributes,
              formGroup: formData,
            };
            predicateDataWithFormData.push(predicateWithFormData);
          }
        } else if (
          key === predicateData.nodeName &&
          (value === null || value === "")
        ) {
          let predicateWithFormData: PredicateData = {
            nodeName: predicateData.nodeName,
            nodePath: predicateData.nodePath,
            attributes: predicateData.attributes,
            formGroup: this._fb.group({
              groupArray: this._fb.array([]),
            }),
          };
          predicateDataWithFormData.push(predicateWithFormData);
        }
      });
    });
    return predicateDataWithFormData;
  }
  //here we are just adding the empty form group as the filters are not there and then we will return predicate data
  generateTheDataWhenNoFilters(
    listOfPredicateData: PredicateData[]
  ): PredicateData[] {
    return listOfPredicateData.map((item: any) => ({
      nodeName: item.nodeName,
      nodePath: item.nodePath,
      attributes: item.attributes,
      formGroup: this._fb.group({
        groupArray: this._fb.array([]),
      }),
    }));
  }
  checkForSimpleFilter(filterValue: string): boolean {
    let regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    return regex.test(filterValue);
  }
  checkForGroupFilter(filterValue: string): boolean {
    let regex =
      /^\[\s*\(\s*@\w+\s*[=!<>~]{1,3}\s*'[^']+'\s*(\s*(AND|OR)\s*@\w+\s*[=!<>~]{1,3}\s*'[^']+'\s*)+\)\s*\]$/;
    return regex.test(filterValue);
  }
  checkForNestedFilter(filterValue: string): boolean {
    let regex =
      /^\[\s*\(.*?\)\s+(AND|OR)\s+\(.*?\)(\s+(AND|OR)\s+\(.*?\))*\s*\]$/;
    return regex.test(filterValue);
  }
  generateFormDataForSimpleFilter(
    filterValue: string,
    logicalOperator = this.defaultLogicalOperatorForSimpleFilter
  ): FormGroup {
    const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    const values = filterValue.match(regex);
    const condition = values[2];
    const value = values[3];
    const attribute = values[1];
    if (values) {
      let formGroup = this._fb.group({
        groupArray: this._fb.array([
          this._fb.group({
            logicalOperator: this._fb.control(
              logicalOperator,
              Validators.required
            ),
            conditions: this._fb.array([
              this.createConditionGroup(attribute, condition, value),
            ]),
          }),
        ]),
      });
      return formGroup;
    }
  }
  generateFormDataForGroup(filterValue: string): FormGroup {
    const conditionsAndOperators =
      this.extractConditionsAndOperator(filterValue);
    const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
    const multipleFormGroups: FormGroup[] = [];
    if (
      conditionsAndOperators.conditions.length > 1 &&
      (conditionsAndOperators.operator !== null ||
        conditionsAndOperators.operator !== "")
    ) {
      conditionsAndOperators.conditions.forEach((item: string) => {
        const values = item.match(regex);
        const condition = values[2];
        const value = values[3];
        const attribute = values[1];
        let singleFormGroup = this.createConditionGroup(
          attribute,
          condition,
          value
        );
        multipleFormGroups.push(singleFormGroup);
      });
    }

    let formGroup = this._fb.group({
      groupArray: this._fb.array([
        this._fb.group({
          logicalOperator: this._fb.control(
            conditionsAndOperators.operator,
            Validators.required
          ),
          conditions: this._fb.array(multipleFormGroups),
        }),
      ]),
    });
    return formGroup;
  }
  generateFormDataForNestedFilter(filterValue: string): FormGroup {
    //first we want all the groups and operators separated from the nested string, here we will get all the nested groups as array of strings and also operators as array of strings returned by the "extractNestedConditions()" method.
    let groupsAndCondition = this.extractNestedConditions(filterValue);
    const multipleOuterFormGroups: FormGroup[] = [];

    groupsAndCondition.groups.forEach((group: string) => {
      const multipleInnerFormGropus: FormGroup[] = [];
      const conditionsAndOperators = this.extractConditionsAndOperator(group);
      const regex = /\[\s*(@\w+)\s*([^\s'\[\]]+)\s*'([^']+)'\s*\]/;
      // here in this below if condition we are checking whether if the incoming conditions has just one or more than one conditons. if the incoming group has more than one condition like "(@Services!='no' AND @Status='valid')" then it will have operator but if it has just one condition like "(@orderno='654321')" then it will not have any operator so we will add the default operator by ourself and that is what is done here.
      if (
        conditionsAndOperators.operator === null ||
        conditionsAndOperators.operator === ""
      ) {
        conditionsAndOperators.operator =
          this.defaultLogicalOperatorForSimpleFilter;
      }

      conditionsAndOperators.conditions.forEach((item: string) => {
        const values = item.match(regex);
        const condition = values[2];
        const value = values[3];
        const attribute = values[1];
        let singleFormGroup = this.createConditionGroup(
          attribute,
          condition,
          value
        );
        multipleInnerFormGropus.push(singleFormGroup);
      });

      let formGroup = this._fb.group({
        logicalOperator: this._fb.control(
          conditionsAndOperators.operator,
          Validators.required
        ),
        conditions: this._fb.array(multipleInnerFormGropus),
      });
      multipleOuterFormGroups.push(formGroup);
    });

    let formGroup = this._fb.group({
      groupArray: this._fb.array(multipleOuterFormGroups),
    });
    return formGroup;
  }
  createConditionGroup(
    attribute: string,
    condition: string,
    value: string
  ): FormGroup {
    return this._fb.group({
      attribute: this._fb.control(attribute, Validators.required),
      condition: this._fb.control(condition, Validators.required),
      value: this._fb.control(value, Validators.required),
    });
  }
  extractNestedConditions(filterValue: string) {
    let nestedGroups: NestedGroups;
    // step 1 - remove the '[' and ']'
    const trimmed = filterValue.trim().slice(1, -1).trim();

    // step 2 - match all the groups with '(' and ')' and extract them
    const groups = trimmed.match(/\(.*?\)/g);

    // step 3 - extracting all the group operators in the same order as they are present
    const operators = trimmed
      .split(/\(.*?\)/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);

    nestedGroups = { groups: groups, operators: operators };

    return nestedGroups;
  }
  extractConditionsAndOperator(filterValue: string): ConditionGroup {
    let conditionsAndOperators: ConditionGroup;
    // Step 1: Remove brackets
    const cleaned = filterValue.replace(/[\[\]\(\)]/g, "");

    // Step 2: Extract the operator (first match is enough, since they're all same)
    const operatorMatch = cleaned.match(/\b(AND|OR)\b/);
    const operator = operatorMatch ? operatorMatch[0] : null;

    // Step 3: Split the conditions by that operator
    const rawConditions = operator
      ? cleaned.split(new RegExp(`\\s+${operator}\\s+`))
      : [cleaned]; // if no operator found, return the full condition as a single entry

    //we adding the square braces [] to each of the filters
    const conditions = rawConditions.map((cond) => `[${cond.trim()}]`);
    conditionsAndOperators = { conditions: conditions, operator: operator };
    return conditionsAndOperators;
  }
  //#endregion
}
